import { createContext, ReactNode, useContext, useMemo } from 'react';

import { BuildCacheToolIds } from '@/models/BuildCacheTool';
import { BuildToolInvocationWithDetails } from '@/models/BuildToolInvocation';

type InvocationListTableContextType = {
  isBuildSpecific: boolean;
  isProjectSpecific: boolean;
  isFiltered: boolean;
  isInSelectMode: boolean;
  invocationsToCompare: BuildToolInvocationWithDetails[];
  setInvocationsToCompare?: (value: BuildToolInvocationWithDetails[]) => void;
  page: number;
  setPage: (page: number) => void;
  itemCount: number;
  itemsPerPage: number;
  setItemsPerPage: (itemsPerPage: number) => void;
  selectedTool: BuildCacheToolIds;
};

const InvocationListTableContext = createContext<InvocationListTableContextType>({
  isBuildSpecific: false,
  isProjectSpecific: false,
  isFiltered: false,
  isInSelectMode: false,
  invocationsToCompare: [],
  setInvocationsToCompare: undefined,
  page: 0,
  setPage: () => {},
  itemCount: 0,
  itemsPerPage: 0,
  setItemsPerPage: () => {},
  selectedTool: BuildCacheToolIds.gradle,
});

type InvocationListTableContextProviderProps = InvocationListTableContextType & {
  children: ReactNode;
};

export const InvocationListTableContextProvider = ({
  children,
  isBuildSpecific,
  isProjectSpecific,
  isFiltered,
  isInSelectMode = false,
  invocationsToCompare,
  setInvocationsToCompare,
  page,
  setPage,
  itemCount,
  itemsPerPage,
  setItemsPerPage,
  selectedTool,
}: InvocationListTableContextProviderProps) => {
  const context = useMemo(
    () => ({
      isBuildSpecific,
      isProjectSpecific,
      isFiltered,
      isInSelectMode,
      invocationsToCompare,
      setInvocationsToCompare,
      page,
      setPage,
      itemCount,
      itemsPerPage,
      setItemsPerPage,
      selectedTool,
    }),
    [
      isBuildSpecific,
      isProjectSpecific,
      isFiltered,
      isInSelectMode,
      invocationsToCompare,
      setInvocationsToCompare,
      page,
      setPage,
      itemCount,
      itemsPerPage,
      setItemsPerPage,
      selectedTool,
    ],
  );

  return <InvocationListTableContext.Provider value={context}>{children}</InvocationListTableContext.Provider>;
};

export const useInvocationListTableContext = () => useContext(InvocationListTableContext);
